
import { Component, Vue } from "vue-property-decorator";
import OrdersHeader from "@/components/orders/OrdersHeader.vue";
import { Action, Getter } from "vuex-class";
import RequestHistorialTable from "@/components/orders/RequestHistorialTable.vue";
import {
  ICountryConfig,
  ICountryConfigResponse,
  IRequestHistorialsService,
  ISolCustomerCancellationReason,
  ISolCustomerPack,
  IUserClientData,
  IUserClientReportTypeData,
  IUserClientSubclientData,
  IUserClientSubclientServicepointData,
  IUserDetailData,
} from "@/entities";
import Multiselect from "vue-multiselect";
import {
  IRequestClientsReport,
  IRequestHistorialParams,
  IRequestHistorialResponse,
  ISolCustomerCancellationReasonResponse,
  ISolCustomerPackParams,
  ISolCustomerPackResponse,
} from "@/services/backend/sol_customer/orders/orders.types";
import NewOrderModal from "@/components/orders/NewOrderModal.vue";
import { ISolCustomerCurrencyParams } from "@/services";
import { MODULE } from "@/constans";
import WarningModal from "@/components/common/WarningModal.vue";
import { SweetAlertResult } from "sweetalert2";
import AsyncComputed from "vue-async-computed-decorator";

@Component({
  name: "orders-page",
  components: { OrdersHeader, RequestHistorialTable, Multiselect, NewOrderModal, WarningModal },
})
export default class OrdersPage extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;
  @Getter("getCashCountryConfig") cashCountryConfig!: ICountryConfigResponse | undefined;
  @Action("fetchRequestHistorial") requestHistorial!: (
    params: IRequestHistorialParams
  ) => Promise<IRequestHistorialResponse | undefined>;
  @Action("fetchSolCustomerCurrency") fetchSolCustomerCurrency!: (
    params: ISolCustomerCurrencyParams
  ) => Promise<void>;
  @Action("fetchCancellationReasonsService") fetchCancellationReasonsService!: (
    countryCode: string
  ) => Promise<ISolCustomerCancellationReasonResponse | undefined>;
  @Action("generateOrdersReport") generateOrdersReport!: (
    request: IRequestClientsReport
  ) => Promise<void>;
  @Action("fetchTypeClientsReport") fetchTypeClientReport!: () => Promise<
    IUserClientReportTypeData[] | undefined
  >;
  @Action("fetchSolCustomerPacks") fetchSolCustomerPacks!: (
    params: ISolCustomerPackParams
  ) => Promise<ISolCustomerPackResponse | undefined>;

  clientSelected: IUserClientData | null = null;
  subclientSelected: IUserClientSubclientData | null = null;
  servicepointSelected: IUserClientSubclientServicepointData | null = null;
  pendingServices: IRequestHistorialsService[] = [];
  todayServices: IRequestHistorialsService[] = [];
  prevServices: IRequestHistorialsService[] = [];
  nextServices: IRequestHistorialsService[] = [];
  cancelledServices: IRequestHistorialsService[] = [];
  cancellationReasons: ISolCustomerCancellationReason[] | undefined = [];
  typeClientsReport: IUserClientReportTypeData[] | undefined = [];
  showCanceled = true;
  userHasServicePoints: boolean | undefined = false;

  constructor() {
    super();
  }

  async mounted(): Promise<void> {
    if (this.countryConfig?.Orders.ShowWarning) {
      this.$nextTick(() => {
        this.$bvModal.show("warning-modal");
      });
    }
    const countryCodeFromUser = this.getLoggedUser?.country?.code || "";
    await this.setupCustomerCurrency(countryCodeFromUser);
    await this.setupCancellationConfiguration(countryCodeFromUser);
    await this.setupRequestsHistorial();

    this.typeClientsReport = await this.fetchTypeClientReport();
  }

  async setupCustomerCurrency(countryCode: string): Promise<void> {
    await this.fetchSolCustomerCurrency({ countryCode });
  }

  async setupCancellationConfiguration(countryCode: string): Promise<void> {
    const cancellationReasonsResponse = this.enabledCancelOrders
      ? await this.fetchCancellationReasonsService(countryCode)
      : null;

    this.cancellationReasons = cancellationReasonsResponse?.cancellationReasons || [];
  }

  async setupRequestsHistorial(): Promise<void> {
    this.userHasServicePoints =
      this.getLoggedUser?.clients?.some((client) =>
        client.subclients?.some((subclient) => (subclient.servicepoints ?? []).length > 0)
      ) || false;

    this.subclientSelected = this.clientSelected?.subclients
      ? this.filterSubclientss(this.clientSelected?.subclients)[0]
      : null;
    this.servicepointSelected = this.servicepoints[0] || null;
    await this.fetchRequestHistorial();
  }

  async fetchPendingOrders(): Promise<void> {
    const res = await this.$services.order_pending.getOrdersPending(
      this.servicepointSelected?.oidSol
    );
    if (res) {
      this.pendingServices = res;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const lowerLimit = new Date(today);
      lowerLimit.setDate(lowerLimit.getDate() - 7);

      const upperLimit = new Date(today);
      upperLimit.setDate(upperLimit.getDate() + 7);
      upperLimit.setHours(23, 59, 59, 999);

      this.pendingServices = res.filter((service: any) => {
        const serviceDate = new Date(service.createdAt);
        return serviceDate >= lowerLimit && serviceDate <= upperLimit;
      });
    }
  }

  messageInformingNoServicePoints(): void {
    this.$swal({
      title: this.$t("label.warning.title").toString(),
      text: this.$t("message.no.servicepoints").toString(),
      icon: "warning",
      confirmButtonText: this.$t("label.ok").toString(),
    });
  }

  get enabledCancelOrders(): boolean {
    return this.cashCountryConfig?.enabledCancelOrders ?? false;
  }

  get enabledPlanningDate(): boolean {
    return this.cashCountryConfig?.enabledPlanningDate ?? false;
  }

  get clients(): IUserClientData[] | [] {
    if (!this.clientSelected && this.getLoggedUser?.clients?.length) {
      this.clientSelected = this.filterClients(this.getLoggedUser?.clients)[0] || null;
    }
    return this.filterClients(this.getLoggedUser?.clients);
  }

  get subclients(): IUserClientSubclientData[] {
    this.subclientSelected = null;
    return this.filterSubclientss(this.clientSelected?.subclients);
  }

  get servicepoints(): IUserClientSubclientServicepointData[] | [] {
    if (
      !this.subclientSelected?.servicepoints?.find(
        (servicepoint) => servicepoint === this.servicepointSelected
      )
    ) {
      this.servicepointSelected = null;
    }

    return this.filterServicepoints(this.subclientSelected?.servicepoints);
  }

  @AsyncComputed()
  async packs(): Promise<ISolCustomerPack[]> {
    if (this.clientSelected?.oidSol) {
      const params = {
        countryCode: this.getLoggedUser?.country?.code,
        entityId: this.clientSelected.oidSol,
      } as ISolCustomerPackParams;

      const res = await this.fetchSolCustomerPacks(params);

      return res?.packs || [];
    }
    return [];
  }

  itemDisabled(type: string): boolean {
    return (
      (type === "POINT" && !this.servicepointSelected) ||
      (type === "CENTER" && !this.subclientSelected) ||
      !this.clientSelected
    );
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.confirmation.to.export.message").toString(),
      text: this.$t("label.confirmation.to.export.message.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }

  async onGenerateOrdersReport(type: string): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();

    if (isConfirmed) {
      const request: IRequestClientsReport = {
        client: this.clientSelected,
        subclient: this.subclientSelected,
        servicepoint: this.servicepointSelected,
        listClients: this.clients,
        listSubClients: this.subclients,
        listServicePoints: this.servicepoints,
        countryCode: this.getLoggedUser.country?.code || "",
        lowerLimitSearch: this.countryConfig.Orders.Filter?.PreviousDays || 7,
        upperLimitSearch: this.countryConfig.Orders.Filter?.NextDays || 7,
        type: type,
      };

      await this.generateOrdersReport(request);

      this.makeToast("success", this.$t("message.usersreport.ok").toString());
    }
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  filterClients(clients: IUserClientData[] | undefined): IUserClientData[] {
    return clients?.filter((c) => this.filterSubclientss(c.subclients).length) || [];
  }

  filterSubclientss(
    subclients: IUserClientSubclientData[] | undefined
  ): IUserClientSubclientData[] {
    return subclients?.filter((s) => this.filterServicepoints(s.servicepoints).length) || [];
  }

  filterServicepoints(
    servicepoints: IUserClientSubclientServicepointData[] | undefined
  ): IUserClientSubclientServicepointData[] {
    return (
      servicepoints?.filter((s) => {
        return s?.adapters?.filter((a) => {
          return (
            a.code === MODULE.ASSISTANCE_SERVICE.toString() ||
            a.code === MODULE.DELIVERY.toString() ||
            a.code === MODULE.DELIVERY_EXCHHANGE.toString() ||
            a.code === MODULE.PICKUP.toString() ||
            a.code === MODULE.SUPPLY_MATERIAL_SERVICE.toString() ||
            a.code === MODULE.SOL_CUSTOMER.toString()
          );
        }).length;
      }) || []
    );
  }

  get entityId(): string | undefined {
    return this.clientSelected?.oidSol;
  }

  async fetchRequestHistorial(): Promise<void> {
    this.pendingServices = [];
    this.todayServices = [];
    this.prevServices = [];
    this.nextServices = [];
    this.cancelledServices = [];

    if (this.userHasServicePoints && this.servicepointSelected?.oidSol) {
      const params: IRequestHistorialParams = {
        servicePointId: this.servicepointSelected?.oidSol || "",
        countryCode: this.getLoggedUser.country?.code || "",
        lowerLimitSearch: this.countryConfig.Orders.Filter?.PreviousDays || 7,
        upperLimitSearch: this.countryConfig.Orders.Filter?.NextDays || 7,
      };

      await this.fetchPendingOrders();
      const res = await this.requestHistorial(params);

      if (res) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date();
        tomorrow.setHours(24, 0, 0, 0);
        this.loadTodayServices(res, today, tomorrow);
        this.loadPrevServices(res, today);
        this.loadNextServices(res, tomorrow);
        this.loadCancelledServices(res);
      }
    } else if (!this.userHasServicePoints) {
      this.messageInformingNoServicePoints();
    }
  }

  loadNextServices(res: IRequestHistorialResponse, tomorrow: Date): void {
    this.nextServices =
      res.requestHistorials?.services?.filter((service: IRequestHistorialsService) => {
        const serviceDateString =
          service.realInitialTime ?? service.plannedInitialTime ?? service.requestInitialTime;

        const serviceDate = serviceDateString ? new Date(serviceDateString) : null;
        return (
          service.serviceStatusCode !== "CANCELLED" &&
          (!serviceDate || (serviceDate && serviceDate >= tomorrow))
        );
      }) || [];
  }

  loadPrevServices(res: IRequestHistorialResponse, today: Date): void {
    this.prevServices =
      res.requestHistorials?.services?.filter((service: IRequestHistorialsService) => {
        const serviceDateString =
          service.realInitialTime ?? service.plannedInitialTime ?? service.requestInitialTime;

        const serviceDate = serviceDateString ? new Date(serviceDateString) : null;
        return service.serviceStatusCode !== "CANCELLED" && serviceDate && serviceDate < today;
      }) || [];
  }

  loadTodayServices(res: IRequestHistorialResponse, today: Date, tomorrow: Date): void {
    this.todayServices =
      res.requestHistorials?.services?.filter((service: IRequestHistorialsService) => {
        const serviceDateString =
          service.realInitialTime ?? service.plannedInitialTime ?? service.requestInitialTime;

        const serviceDate = serviceDateString ? new Date(serviceDateString) : null;
        return (
          service.serviceStatusCode !== "CANCELLED" &&
          serviceDate &&
          serviceDate >= today &&
          serviceDate < tomorrow
        );
      }) || [];
  }

  loadCancelledServices(res: IRequestHistorialResponse): void {
    this.cancelledServices =
      res.requestHistorials?.services?.filter((service: IRequestHistorialsService) => {
        return service.serviceStatusCode === "CANCELLED";
      }) || [];
  }

  handleOpenNewOrderModal(): void {
    if (!this.userHasServicePoints) {
      this.messageInformingNoServicePoints();
      return;
    }
    this.$bvModal.show("newOrderModal");
  }
}
